<template>
  <div>
    <div v-if="!hasData" class="empty-state">Ingen data tillgänglig</div>
    <div v-else class="stacked-chart">
      <div class="chart-y-axis">
        <span v-for="tick in yTicks" :key="tick" class="y-tick">
          {{ tick }}
        </span>
      </div>
      <div class="chart-bars">
        <div v-for="m in monthlyValues" :key="m.month" class="bar-group">
          <div class="stacked-bar">
            <div
              v-if="m.value"
              class="bar-segment"
              :style="{
                height: (animated ? barH(m.value) : 0) + '%'
              }"
              @mousemove="onHover($event, tooltipText(m))"
              @mouseleave="onLeave"
            ></div>
          </div>
          <span class="bar-label">{{ m.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Maj',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dec'
];

function niceStep(max, divisions = 4) {
  if (!max || max <= 0) return 1;
  const rough = max / divisions;
  if (rough <= 1) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  let nice;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 2.5) nice = 2.5;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude;
}

function niceTicks(max) {
  if (!max || max <= 0) return [0];
  const step = niceStep(max);
  const ticks = [0];
  let v = step;
  while (v < max) {
    ticks.push(v);
    v += step;
  }
  ticks.push(v);
  return ticks.reverse();
}

export default {
  props: {
    monthly: {
      type: Object,
      default: () => ({})
    },
    selectedYear: {
      type: String,
      default: 'all'
    }
  },
  emits: ['tooltip'],
  data() {
    return { animated: false };
  },
  computed: {
    monthlyValues() {
      const result = [];
      for (let i = 1; i <= 12; i++) {
        const mm = String(i).padStart(2, '0');
        const byYear = {};
        let sum = 0;
        for (const [year, yearData] of Object.entries(this.monthly || {})) {
          if (this.selectedYear !== 'all' && year !== this.selectedYear) {
            continue;
          }
          const key = `${year}-${mm}`;
          const val = yearData?.[key];
          if (val === undefined || val === null) continue;
          const num = Number(val) || 0;
          byYear[year] = num;
          sum += num;
        }
        result.push({
          month: mm,
          label: MONTH_LABELS[i - 1],
          value: sum,
          byYear
        });
      }
      return result;
    },
    maxValue() {
      return Math.max(...this.monthlyValues.map(m => m.value), 1);
    },
    hasData() {
      return this.monthlyValues.some(m => m.value > 0);
    },
    yTicks() {
      return niceTicks(this.maxValue);
    },
    topTick() {
      return this.yTicks[0] || this.maxValue;
    }
  },
  watch: {
    selectedYear() {
      this.animated = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.animated = true;
        });
      });
    }
  },
  mounted() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.animated = true;
      });
    });
  },
  methods: {
    barH(val) {
      if (!val) return 0;
      return Math.max(0.5, (val / this.topTick) * 95);
    },
    tooltipText(m) {
      if (this.selectedYear !== 'all') {
        return `${m.label}: ${m.value} st`;
      }
      const years = Object.keys(m.byYear).sort();
      if (!years.length) return `${m.label}: 0`;
      const parts = years.map(y => `${y}: ${m.byYear[y]}`);
      return `${m.label} — ${parts.join(', ')}`;
    },
    onHover(event, text) {
      this.$emit('tooltip', {
        visible: true,
        text,
        x: event.clientX,
        y: event.clientY
      });
    },
    onLeave() {
      this.$emit('tooltip', { visible: false, text: '', x: 0, y: 0 });
    }
  }
};
</script>
<style scoped>
.stacked-chart {
  display: flex;
  gap: 8px;
  height: 360px;
  align-items: stretch;
}

.chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 30px;
  flex-shrink: 0;
}

.y-tick {
  font-size: 1rem;
  color: #878b9d;
  text-align: right;
}

.chart-bars {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.bar-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
}

.stacked-bar {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
  max-width: 40px;
  flex: 1;
}

.bar-segment {
  width: 100%;
  min-height: 0;
  background: var(--cat-ba);
  transition:
    height 0.5s cubic-bezier(0.25, 0.1, 0.25, 1),
    opacity 0.2s ease;
  cursor: pointer;
}

.bar-segment:hover {
  opacity: 0.85;
}

.bar-label {
  font-size: 1rem;
  color: #878b9d;
  margin-top: 6px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
  color: #878b9d;
  font-size: 1.3rem;
}
</style>
